import React from 'react';
import Icon from './Icon';

/**
 * SitePopup Component - Template-based rendering
 * Displays site-wide notification popup to visitors
 * Session-based dismissal tracking using sessionStorage
 */
const SitePopup = ({ config, onClose }) => {
  if (!config) return null;

  const {
    templateName,
    popupTitle,
    popupMessage,
    backgroundStyle,
    textStyle,
    customBackgroundColor,
    customTextColor,
    buttonText,
    buttonAction,
    buttonLink,
    fileUrl,
    fileName,
    forceFullscreen = false  // NEW: Force fullscreen interaction option
  } = config;

  // Get background color based on style
  const getBackgroundColor = () => {
    switch (backgroundStyle) {
      case 'light': return '#ffffff';
      case 'dark': return '#1f2937';
      case 'brand': return '#ea580c';
      case 'custom': return customBackgroundColor || '#ffffff';
      default: return '#ffffff';
    }
  };

  // Get text color based on style
  const getTextColor = () => {
    if (textStyle === 'dark') return '#000000';
    if (textStyle === 'light') return '#ffffff';
    
    // Auto mode - determine based on background
    const bg = getBackgroundColor();
    const hex = bg.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  const handleClose = () => {
    // Mark popup as closed in sessionStorage (once per browsing session)
    sessionStorage.setItem('popupDismissed', 'true');
    if (onClose) onClose();
  };

  const handleButtonClick = () => {
    if (buttonAction === 'dismiss') {
      handleClose();
    } else if (buttonAction === 'link' && buttonLink) {
      window.open(buttonLink, '_blank');
      handleClose();
    } else if (buttonAction === 'download' && fileUrl) {
      window.open(fileUrl, '_blank');
      handleClose();
    }
  };

  const bgColor = getBackgroundColor();
  const textColor = getTextColor();
  const buttonBgColor = backgroundStyle === 'brand' ? '#ffffff' : '#ea580c';
  const buttonTextColor = backgroundStyle === 'brand' ? '#000000' : '#ffffff';

  // Render based on template
  const renderTemplate = () => {
    // Fullscreen backdrop class - blocks all interaction when forceFullscreen is enabled
    const backdropClass = forceFullscreen 
      ? "fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4 z-[99999] animate-fadeIn" 
      : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] animate-fadeIn";
    
    switch (templateName) {
      case 'simple_alert':
        return (
          <div className={backdropClass}>
            <div
              className="popup-container max-w-md w-full p-6 rounded-lg shadow-2xl relative animate-slideUp"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              {/* Only show close button if NOT in fullscreen mode */}
              {!forceFullscreen && (
                <button
                  onClick={handleClose}
                  className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-200 hover:bg-opacity-20 transition duration-200"
                  style={{ color: textColor }}
                  aria-label="Close popup"
                >
                  <Icon name="x" size={24} />
                </button>
              )}

              <h3 className="text-xl font-bold mb-3 pr-8">{popupTitle}</h3>
              <p className="mb-4">{popupMessage}</p>

              {fileUrl && fileName && (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm mb-4 hover:underline"
                  style={{ color: textColor }}
                >
                  <Icon name="file" size={16} className="mr-2" />
                  {fileName}
                </a>
              )}

              <button
                onClick={handleButtonClick}
                className="w-full py-2 px-4 rounded font-semibold transition hover:opacity-90"
                style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
              >
                {buttonText}
              </button>
            </div>
          </div>
        );

      case 'image_text':
        return (
          <div className={backdropClass}>
            <div
              className="popup-container max-w-2xl w-full rounded-lg shadow-2xl overflow-hidden relative flex animate-slideUp"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              {/* Image section */}
              <div className="w-1/3 bg-gray-700 bg-opacity-30 flex items-center justify-center">
                {fileUrl && fileName && fileName.match(/\.(jpg|jpeg|png)$/i) ? (
                  <img src={fileUrl} alt={popupTitle} className="w-full h-full object-cover" />
                ) : (
                  <Icon name="image" size={64} style={{ color: textColor, opacity: 0.3 }} />
                )}
              </div>

              {/* Text section */}
              <div className="w-2/3 p-6">
                {/* Only show close button if NOT in fullscreen mode */}
                {!forceFullscreen && (
                  <button
                    onClick={handleClose}
                    className="absolute top-2 right-2 p-2 rounded-full hover:bg-gray-200 hover:bg-opacity-20 transition duration-200"
                    style={{ color: textColor }}
                    aria-label="Close popup"
                  >
                    <Icon name="x" size={20} />
                  </button>
                )}

                <h3 className="text-xl font-bold mb-3 pr-8">{popupTitle}</h3>
                <p className="mb-4 text-sm">{popupMessage}</p>

                <button
                  onClick={handleButtonClick}
                  className="py-2 px-4 rounded font-semibold transition hover:opacity-90"
                  style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
                >
                  {buttonText}
                </button>
              </div>
            </div>
          </div>
        );

      case 'centered_cta':
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] animate-fadeIn">
            <div
              className="popup-container max-w-lg w-full p-8 rounded-lg shadow-2xl relative text-center animate-slideUp"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-200 hover:bg-opacity-20 transition duration-200"
                style={{ color: textColor }}
                aria-label="Close popup"
              >
                <Icon name="x" size={24} />
              </button>

              <h3 className="text-2xl font-bold mb-4">{popupTitle}</h3>
              <p className="text-lg mb-6">{popupMessage}</p>

              {fileUrl && fileName && (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center text-sm mb-4 hover:underline"
                  style={{ color: textColor }}
                >
                  <Icon name="file" size={16} className="mr-2" />
                  {fileName}
                </a>
              )}

              <button
                onClick={handleButtonClick}
                className="py-3 px-6 rounded-lg font-semibold text-lg transition hover:opacity-90"
                style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
              >
                {buttonText}
              </button>
            </div>
          </div>
        );

      case 'announcement_bar':
        return (
          <div className="fixed top-0 left-0 right-0 z-[9999] animate-slideDown">
            <div
              className="popup-container py-3 px-6 shadow-lg flex items-center justify-between"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              <div className="flex-1 flex items-center">
                <span className="font-bold mr-2">{popupTitle}</span>
                <span className="text-sm">{popupMessage}</span>
                {fileUrl && fileName && (
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-3 flex items-center text-xs hover:underline"
                    style={{ color: textColor }}
                  >
                    <Icon name="file" size={14} className="mr-1" />
                    {fileName}
                  </a>
                )}
              </div>
              <button
                onClick={handleButtonClick}
                className="py-1 px-4 rounded font-semibold text-sm ml-4 transition hover:opacity-90 whitespace-nowrap"
                style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
              >
                {buttonText}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderTemplate()}
      
      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideDown {
          from {
            transform: translateY(-100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }

        .animate-slideDown {
          animation: slideDown 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default SitePopup;
